import React, { useCallback, useState } from 'react';
import Image from '@/lib/app-image';
import { Root } from './list/flightlist/types';
import { formatCurrency } from '../lib/utils';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from './ui/button';
import { FLIGHT_TABS } from '@/lib/contants';
import FlightInfo from './list/flightlist/FlightInfo';
import { MoveRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import FareDetails from './list/flightlist/FareDetails';
import BaggageRules from './list/flightlist/BaggageRules';
import {
  setSelectedFlight,
  setSelectedReturnFlight,
} from '@flysync/shared-redux/slices/userInputSlice';
import BucketFare from './modals/BucketFare';
import { getAirlineIconUrl } from '@/lib/airlineIcon';
import {
  setSelectBucket,
  setReturnBucket,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import FareRule from './fareRule';
import { useFareRuleMutation } from '@flysync/shared-redux/services/booking';
import {
  FormattedFareRuleData,
  formatFareRuleError,
  formatFetchFareRuleResponse,
} from '@/lib/fare-rule-formatter';

function SelectedFlightInfo() {
  const [reportsOpen, setReportsOpen] = useState(false);
  const [fareRuleData, setFareRuleData] = useState<
    Record<string, FormattedFareRuleData>
  >({});
  const [fareRuleLoader, setFareRuleLoader] = useState<Record<string, boolean>>(
    {},
  );
  const [getFareRule] = useFareRuleMutation();
  const userInput = useSelector(
    (state: { userInputs: any }) => state.userInputs,
  );
  const flightSearchInput = useSelector((state: any) => state.flightSearch);

  const getFareRuleKey = useCallback((flight: any, index: number) => {
    const priceDetails = flight?.price_details?.[0];

    return [
      index,
      flight?.airline_code,
      flight?.flight_number,
      flight?.origin,
      flight?.destination,
      flight?.departure_date,
      flight?.departure_time,
      priceDetails?.ticket_id ||
        priceDetails?.rand_id ||
        priceDetails?.flightRecordId ||
        priceDetails?.r_flight_itinerary_id,
    ]
      .map((value) => value ?? '')
      .join('|');
  }, []);

  const buildFareRuleRequest = useCallback(
    (flight: any) => {
      const priceDetails = flight?.price_details?.[0];

      if (!flight || !priceDetails) {
        return null;
      }

      return {
        airline_code: flight?.airline_code,
        airline_logo: flight?.airline_logo,
        airline_name: flight?.airline_name,
        flight_number: flight?.flight_number,
        origin: flight?.origin,
        destination: flight?.destination,
        departure_time: flight?.departure_time,
        departure_date: flight?.departure_date,
        departure_terminal:
          flight?.departure_terminal ?? flight?.origin_terminal,
        duration_stops: flight?.duration_stops,
        stop_count: flight?.stop_count,
        travel_time: flight?.travel_time,
        arrival_time: flight?.arrival_time,
        arrival_date: flight?.arrival_date,
        arrival_terminal:
          flight?.arrival_terminal ?? flight?.destination_terminal,
        travel_date: flight?.travel_date,
        origin_airport: flight?.origin_airport,
        destination_airport: flight?.destination_airport,
        supplier: priceDetails?.supplier_code,
        travel_type: flight?.trip_type ?? flightSearchInput?.travelMode,
        trip_type: flightSearchInput?.tripType,
        cabin_type: flightSearchInput?.passengers?.cabinId ?? 'EC',
        fareTypeId: priceDetails?.fareTypeId,
        refundType: priceDetails?.refundableType,
        departureDate: flight?.departure_date,
        bookingClass: priceDetails?.popover_info?.[1]?.value ?? '',
        fareBasis: priceDetails?.popover_info?.[2]?.value ?? '',
        price_details: priceDetails,
      };
    },
    [
      flightSearchInput?.passengers?.cabinId,
      flightSearchInput?.travelMode,
      flightSearchInput?.tripType,
    ],
  );

  const hasFareRuleData = (fareRule: any) => {
    return !!fareRule && Object.keys(fareRule).length > 0;
  };

  const fetchFareRuleData = useCallback(
    async (flight: any, index: number) => {
      const embeddedFareRule = flight?.price_details?.[0]?.fareRule;

      if (hasFareRuleData(embeddedFareRule)) {
        return;
      }

      const fareRuleKey = getFareRuleKey(flight, index);

      if (fareRuleData[fareRuleKey] || fareRuleLoader[fareRuleKey]) {
        return;
      }

      const fareRuleReq = buildFareRuleRequest(flight);

      if (!fareRuleReq) {
        return;
      }

      setFareRuleLoader((current) => ({
        ...current,
        [fareRuleKey]: true,
      }));

      try {
        const response = await getFareRule({
          data: {
            flowType: 'search',
            product_type: flightSearchInput?.product_type || 'B2C',
            data: [fareRuleReq],
          },
        }).unwrap();

        setFareRuleData((current) => ({
          ...current,
          [fareRuleKey]: formatFetchFareRuleResponse(response),
        }));
      } catch {
        setFareRuleData((current) => ({
          ...current,
          [fareRuleKey]: formatFareRuleError(),
        }));
      } finally {
        setFareRuleLoader((current) => ({
          ...current,
          [fareRuleKey]: false,
        }));
      }
    },
    [
      buildFareRuleRequest,
      fareRuleData,
      fareRuleLoader,
      getFareRule,
      getFareRuleKey,
    ],
  );

  return (
    <div className="sticky bottom-0 z-30 w-full overflow-hidden bg-gray-50/80 backdrop-blur-sm">
      <div className="space-y-2 sm:space-y-3 lg:space-y-4">
        {/* Flight Info Section */}
        <div className="flex w-full items-center overflow-hidden text-gray-600">
          <Info
            outboundFlight={userInput.selectedFlight}
            returnFlight={userInput.selectedReturnFlight}
            callback={() => setReportsOpen(!reportsOpen)}
          />
        </div>

        {/* Expanded Details Section */}
        {reportsOpen && (
          <div className="flex flex-col overflow-x-auto bg-background sm:flex-row">
            {[userInput.selectedFlight, userInput.selectedReturnFlight].map(
              (data: any, index: number) => (
                <div key={index} className="w-full">
                  <Tabs defaultValue="Flight Information" className="w-full">
                    {/* Tabs Header */}
                    <TabsList className="border-light no-scrollbar flex flex-wrap justify-between overflow-x-auto border-b-4 sm:flex-nowrap">
                      {FLIGHT_TABS?.map((item) => (
                        <TabsTrigger
                          key={item?.id}
                          className="border-light -mb-[4px] items-center whitespace-nowrap border-b-4 px-2 py-1.5 text-[11px] font-semibold text-zinc-800 data-[state=active]:border-primary sm:px-3 sm:py-2 sm:text-xs lg:px-4 lg:py-2.5 lg:text-sm"
                          value={item?.value}
                          onClick={() => {
                            if (item?.value === 'Cancellation Rules') {
                              fetchFareRuleData(data, index);
                            }
                          }}
                        >
                          {item?.value}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {/* Tabs Content */}
                    <div className="no-scrollbar max-h-[40vh] overflow-y-auto sm:max-h-[50vh] lg:max-h-[60vh]">
                      <TabsContent
                        value="Flight Information"
                        className="m-0 p-3"
                      >
                        <FlightInfo
                          layoverStopsData={data.layoverStopsData}
                          layoverData={data.layover}
                        />
                      </TabsContent>
                      <TabsContent value="Fare Details" className="m-0 p-3">
                        <FareDetails
                          data={data.price_details?.[0]?.fareDetails}
                        />
                      </TabsContent>
                      <TabsContent value="Baggage Rules" className="m-0 p-3">
                        <BaggageRules
                          data={{
                            ...data.flightInfo[0],
                            ...data.price_details[0],
                          }}
                        />
                      </TabsContent>
                      <TabsContent
                        value="Cancellation Rules"
                        className="m-0 p-3"
                        onClick={() => fetchFareRuleData(data, index)}
                      >
                        <FareRule
                          fareRules={
                            hasFareRuleData(data.price_details?.[0]?.fareRule)
                              ? data.price_details?.[0]?.fareRule
                              : fareRuleData[getFareRuleKey(data, index)]
                          }
                          isLoader={
                            !hasFareRuleData(
                              data.price_details?.[0]?.fareRule,
                            ) && !fareRuleData[getFareRuleKey(data, index)]
                              ? true
                              : fareRuleLoader[getFareRuleKey(data, index)]
                          }
                        />
                      </TabsContent>
                    </div>
                  </Tabs>
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SelectedFlightInfo;

// Define types for FlightSection props
interface FlightSectionProps {
  flight: {
    airline_name?: string;
    airline_code?: string;
    departure_time: string;
    arrival_time: string;
    flight_number: string;
    airline_logo: string;
    price_details?: Array<{
      fareDetails?: {
        total_amount: number | string;
        net_fare: number | string;
        management_fee: number | string;
        management_fee_tax: number | string;
        admin_markup: number | string;
        admin_markup_gst: number | string;
      };
    }>;
  };
  index: number;
  callback: () => void;
  flightSearchInput: any;
}

// Update FlightSection component to use the defined types
const FlightSection: React.FC<FlightSectionProps> = ({
  flight,
  index,
  callback,
  flightSearchInput,
}) => (
  <div className="flex w-full flex-col items-center justify-between gap-4 p-2 sm:flex-row sm:space-y-0 md:items-center md:space-y-2">
    <div className="flex w-full flex-row items-center sm:w-auto sm:items-start">
      <div>
        <p className="text-center text-xs sm:text-left sm:text-xs md:text-sm lg:text-sm">
          {index == 0 ? 'Departure' : 'Return'} &bull; {flight?.airline_code}{' '}
          &bull; {flight?.flight_number}
        </p>
        <div className="flex flex-col items-center sm:flex-row sm:space-x-2 sm:space-y-0">
          <Image
            alt="airline_logo"
            src={getAirlineIconUrl(flight?.airline_logo || 'none')}
            className="h-8 w-8 rounded-full border border-white sm:h-10 sm:w-10"
            crossOrigin="anonymous"
          />
          <div className="text-center sm:text-left">
            <p className="flex items-center justify-center gap-2 text-xs font-semibold sm:justify-start sm:text-xs md:text-base lg:text-lg">
              {flight.departure_time} <MoveRight className="h-3 w-3" />{' '}
              {flight?.arrival_time}
            </p>
            <span
              className="cursor-pointer text-[10px] sm:text-[10px] md:text-sm lg:text-sm"
              onClick={callback}
            >
              Flight Details
            </span>
          </div>
        </div>
      </div>
    </div>
    <p className="text-sm font-semibold sm:text-xs md:text-base lg:text-lg">
      {formatCurrency(
        Math.abs(
          Number(
            flight?.price_details?.[0]?.fareDetails?.net_fare ||
              (index === 0
                ? flightSearchInput.selectBucket[0]?.fareDetails?.net_fare
                : flightSearchInput.returnBucket[0]?.fareDetails?.net_fare),
          ) 
        ),

        // -
        //     Number(
        //       flight?.price_details?.[0]?.fareDetails?.management_fee ||
        //         (index === 0
        //           ? flightSearchInput.selectBucket[0]?.fareDetails
        //               ?.management_fee
        //           : flightSearchInput.returnBucket[0]?.fareDetails
        //               ?.management_fee),
        //     ) -
        //     Number(
        //       flight?.price_details?.[0]?.fareDetails?.management_fee_tax ||
        //         (index === 0
        //           ? flightSearchInput.selectBucket[0]?.fareDetails
        //               ?.management_fee_tax
        //           : flightSearchInput.returnBucket[0]?.fareDetails
        //               ?.management_fee_tax),
        //     ),
      )}
    </p>
  </div>
);

// Helper function to render flight sections
const renderFlightSection = (
  flight: Root,
  index: number,
  callback: () => void,
  flightSearchInput: any,
) => {
  return Object.keys(flight)?.length > 0 ? (
    <FlightSection
      flight={flight}
      index={index}
      callback={callback}
      flightSearchInput={flightSearchInput}
    />
  ) : (
    <div className="flex basis-[35%] bg-white"></div>
  );
};

const Info = ({
  outboundFlight,
  returnFlight,
  callback,
}: {
  outboundFlight: Root;
  returnFlight: Root;
  callback: () => void;
}) => {
  const [showBuckerFare, setShowBucketFare] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const flightSearchInput = useSelector((state: any) => state.flightSearch);
  const isBothFlightSelected =
    Object.keys(outboundFlight ?? {}).length > 0 ||
    Object.keys(returnFlight ?? {}).length > 0;

  const onSelectBook = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    // if (
    //   flightSearchInput?.travelType === 'RT' ||
    //   flightSearchInput?.tripType == 'INRT'
    // ) {
    //   console.log(showBuckerFare, 'flightSearchInput');
    // }
    setShowBucketFare(true);
    // let flightData = {
    //   ...outboundFlight,
    //   ...returnFlight,
    //   price_details: [userInput.selectedBucket],
    // };
    dispatch(setSelectedFlight(outboundFlight));
    flightSearchInput.selectBucket.length == 0 &&
      dispatch(setSelectBucket(outboundFlight.price_details));
    dispatch(setSelectedReturnFlight(returnFlight));
    if (
      flightSearchInput.travelType === 'RT' &&
      flightSearchInput.travelMode === 'DO'
    ) {
      flightSearchInput.returnBucket?.length == 0 &&
        dispatch(setReturnBucket(returnFlight.price_details));
    } else {
      dispatch(setReturnBucket([]));
    }
    // sessionStorage.setItem("USER_INPUT",JSON.stringify(data))
  };

  const calculateNetPrice = (
    flight: any,
    index: number,
    flightSearchInput: any,
  ) => {
    if (flight?.price_details?.[0]?.fareDetails) {
      let fareDetails = flight.price_details[0].fareDetails;
      return Math.abs(
        Number(fareDetails.net_fare)
      );
      // -
      //     Number(fareDetails.management_fee) -
      //     Number(fareDetails.management_fee_tax),
    }
    const { net_fare, management_fee, management_fee_tax } =
      index == 0
        ? flightSearchInput.selectBucket[0]?.fareDetails
        : flightSearchInput.returnBucket[0]?.fareDetails;
    return Math.abs(
      Number(net_fare)
    );
    // - Number(management_fee) - Number(management_fee_tax),
  };

  return (
    <div className="flex w-full flex-col flex-wrap space-y-3 rounded bg-primary p-2 text-white sm:p-3 md:flex-wrap lg:flex-row lg:flex-wrap lg:space-y-0 lg:p-4">
      {/* Flight Sections */}
      <div className="flex w-full flex-row justify-between gap-3 px-2 sm:px-3 lg:flex-1 lg:gap-4">
        {renderFlightSection(outboundFlight, 0, callback, flightSearchInput)}
        {renderFlightSection(returnFlight, 1, callback, flightSearchInput)}
      </div>

      {/* Price and Action Section */}
      <div className="flex flex-row items-center justify-between border-t border-white/30 px-2 pt-2 sm:px-4 lg:basis-[30%] lg:border-l lg:border-t-0 lg:pt-0">
        <div>
          <p className="text-xs font-semibold sm:text-sm">Total</p>
          <div className="text-lg font-semibold sm:text-2xl">
            {formatCurrency(
              (calculateNetPrice(outboundFlight, 0, flightSearchInput) || 0) +
                (calculateNetPrice(returnFlight, 1, flightSearchInput) || 0),
            )}
          </div>
        </div>
        <Button
          disabled={!isBothFlightSelected}
          onClick={(e) => onSelectBook(e)}
          className="bg-white px-3 py-1.5 text-sm font-semibold text-primary sm:px-4 sm:py-2 sm:text-base"
        >
          View Price
        </Button>
      </div>

      <BucketFare showModal={showBuckerFare} setShowModal={setShowBucketFare} />
    </div>
  );
};
